import { useRef } from 'react';
import { Area, Grupo, LIENZO, Mesa, cajaDe, ESTADOS, minutosDesde, duracion } from './mesas';

const pct = (v: number, base: number) => `${(v / base) * 100}%`;
const REJILLA = 10; // al acomodar, todo se alinea a múltiplos de 10
const snap = (v: number) => Math.round(v / REJILLA) * REJILLA;
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

type Caja = { pos_x: number; pos_y: number; ancho: number; alto: number };
export type Seleccion = { tipo: 'mesa'; id: string } | { tipo: 'area'; idx: number } | null;

interface Props {
  grupos: Grupo[];
  areas: Area[];
  // Operación
  selId?: string | null;
  resaltar?: (g: Grupo) => string;        // outline
  atenuar?: (g: Grupo) => boolean;
  onTocar?: (g: Grupo) => void;
  // "Acomodar plano"
  acomodando?: boolean;
  mesasEdicion?: Mesa[];
  seleccion?: Seleccion;
  onSeleccionar?: (s: Seleccion) => void;
  onCambiarMesa?: (id: string, c: Partial<Caja>) => void;
  onCambiarArea?: (idx: number, c: Partial<Caja>) => void;
}

// Tamaños relativos al ancho del plano (1000 unidades): a 1000 px se ven como antes.
const NUM = 'clamp(12px, 2.6cqw, 28px)';
const TXT = 'clamp(9px, 1.4cqw, 15px)';

export default function PlanoSalon(p: Props) {
  const lienzo = useRef<HTMLDivElement>(null);
  const arrastre = useRef<{ x: number; y: number; caja: Caja; modo: 'mover' | 'tamano'; aplicar: (c: Partial<Caja>) => void; min: number } | null>(null);

  const iniciar = (e: React.PointerEvent, caja: Caja, modo: 'mover' | 'tamano', aplicar: (c: Partial<Caja>) => void, min: number) => {
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    arrastre.current = { x: e.clientX, y: e.clientY, caja, modo, aplicar, min };
  };
  const mover = (e: React.PointerEvent) => {
    const a = arrastre.current, r = lienzo.current?.getBoundingClientRect();
    if (!a || !r) return;
    const dx = (e.clientX - a.x) / r.width * LIENZO.w;
    const dy = (e.clientY - a.y) / r.height * LIENZO.h;
    const c = a.caja;
    if (a.modo === 'mover') {
      a.aplicar({ pos_x: clamp(snap(c.pos_x + dx), 0, LIENZO.w - c.ancho), pos_y: clamp(snap(c.pos_y + dy), 0, LIENZO.h - c.alto) });
    } else {
      a.aplicar({ ancho: clamp(snap(c.ancho + dx), a.min, LIENZO.w - c.pos_x), alto: clamp(snap(c.alto + dy), a.min, LIENZO.h - c.pos_y) });
    }
  };
  const soltar = () => { arrastre.current = null; };

  // Flechas: mueven lo seleccionado 10 unidades (accesible sin mouse).
  const teclas = (e: React.KeyboardEvent, caja: Caja, aplicar: (c: Partial<Caja>) => void) => {
    const d: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const v = d[e.key];
    if (!v) return;
    e.preventDefault();
    aplicar({
      pos_x: clamp(caja.pos_x + v[0] * REJILLA, 0, LIENZO.w - caja.ancho),
      pos_y: clamp(caja.pos_y + v[1] * REJILLA, 0, LIENZO.h - caja.alto),
    });
  };

  const agarradera = (caja: Caja, aplicar: (c: Partial<Caja>) => void, min: number) => (
    <span aria-hidden onPointerDown={e => iniciar(e, caja, 'tamano', aplicar, min)} style={{
      position: 'absolute', right: -9, bottom: -9, width: 20, height: 20, borderRadius: 6,
      background: 'var(--brand)', border: '3px solid var(--surface)', cursor: 'nwse-resize', touchAction: 'none',
    }} />
  );

  return (
    <div ref={lienzo} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar}
      onPointerDown={() => p.acomodando && p.onSeleccionar?.(null)}
      style={{
        // Se ajusta al espacio disponible (ancho y alto) sin barras; letras en cqw = proporcionales al plano.
        position: 'relative', width: `min(100cqw, calc(100cqh * ${LIENZO.w} / ${LIENZO.h}))`, margin: '0 auto', aspectRatio: `${LIENZO.w}/${LIENZO.h}`, containerType: 'inline-size',
        backgroundImage: 'radial-gradient(var(--line) 1px, transparent 1px)', backgroundSize: '24px 24px',
        borderRadius: 10, touchAction: p.acomodando ? 'none' : 'auto',
        outline: p.acomodando ? '2px dashed var(--line-strong)' : 'none', outlineOffset: 6,
      }}>
      {p.areas.map((a, i) => {
        const sel = p.acomodando && p.seleccion?.tipo === 'area' && p.seleccion.idx === i;
        const aplicar = (c: Partial<Caja>) => p.onCambiarArea?.(i, c);
        return (
          <div key={a.id ?? `n${i}`} tabIndex={p.acomodando ? 0 : -1}
            onPointerDown={p.acomodando ? e => { p.onSeleccionar?.({ tipo: 'area', idx: i }); iniciar(e, a, 'mover', aplicar, 20); } : undefined}
            onKeyDown={p.acomodando ? e => teclas(e, a, aplicar) : undefined}
            style={{
              position: 'absolute', left: pct(a.pos_x, LIENZO.w), top: pct(a.pos_y, LIENZO.h),
              width: pct(a.ancho, LIENZO.w), height: pct(a.alto, LIENZO.h), borderRadius: 10,
              background: 'repeating-linear-gradient(135deg, var(--line-2) 0 8px, var(--bg) 8px 16px)',
              display: 'grid', placeItems: 'center', fontSize: 'clamp(10px, 1.5cqw, 15px)', fontWeight: 700, color: 'var(--muted)',
              cursor: p.acomodando ? 'move' : 'default', touchAction: 'none',
              outline: sel ? '3px solid var(--brand-texto)' : 'none', outlineOffset: 3,
            }}>
            {a.nombre}
            {sel && agarradera(a, aplicar, 20)}
          </div>
        );
      })}

      {p.acomodando
        ? p.mesasEdicion?.map(m => {
            const caja = m as Caja;
            const sel = p.seleccion?.tipo === 'mesa' && p.seleccion.id === m.id;
            const aplicar = (c: Partial<Caja>) => p.onCambiarMesa?.(m.id, c);
            return (
              <div key={m.id} tabIndex={0} aria-label={`Mesa ${m.numero}`}
                onPointerDown={e => { p.onSeleccionar?.({ tipo: 'mesa', id: m.id }); iniciar(e, caja, 'mover', aplicar, 40); }}
                onKeyDown={e => teclas(e, caja, aplicar)}
                style={{
                  position: 'absolute', left: pct(m.pos_x!, LIENZO.w), top: pct(m.pos_y!, LIENZO.h),
                  width: pct(m.ancho!, LIENZO.w), height: pct(m.alto!, LIENZO.h),
                  borderRadius: m.forma === 'redonda' ? '50%' : 14, background: 'var(--surface)',
                  border: '2px solid var(--ink-2)', display: 'grid', placeItems: 'center',
                  fontSize: NUM, fontWeight: 700, cursor: 'move', touchAction: 'none',
                  outline: sel ? '3px solid var(--brand-texto)' : 'none', outlineOffset: 3,
                }}>
                {m.numero}
                {sel && agarradera(caja, aplicar, 40)}
              </div>
            );
          })
        : p.grupos.map(g => {
            const b = cajaDe(g), st = ESTADOS[g.estado], m = g.principal;
            const redonda = g.miembros.length === 1 && m.forma === 'redonda';
            const min = minutosDesde(m.ocupada_desde);
            let meta = '';
            if (g.estado === 'libre') meta = `${m.capacidad} ${m.capacidad === 1 ? 'lugar' : 'lug.'}`;
            if (g.estado === 'ocupada') meta = redonda ? `${duracion(min)}` : `${m.personas_actual || 0}/${m.capacidad} · ${duracion(min)}`;
            if (g.estado === 'por_cobrar') meta = `${duracion(min)}`;
            if (g.estado === 'unida') meta = `${g.miembros.reduce((s, x) => s + (x.personas_actual || 0), 0)} pers · ${duracion(min)}`;
            return (
              <button key={m.id} onClick={() => p.onTocar?.(g)}
                aria-label={`Mesa ${g.etiqueta}, ${st.label}`} aria-pressed={p.selId === m.id}
                style={{
                  position: 'absolute', left: pct(b.x, LIENZO.w), top: pct(b.y, LIENZO.h),
                  width: pct(b.w, LIENZO.w), height: pct(b.h, LIENZO.h),
                  borderRadius: redonda ? '50%' : 14, background: st.bg, color: st.fg,
                  border: `2px ${st.borde} ${st.bd}`, outline: p.resaltar?.(g) || 'none', outlineOffset: 3,
                  opacity: p.atenuar?.(g) ? 0.25 : 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '0.3cqw', padding: 4, cursor: 'pointer', overflow: 'hidden',
                  textAlign: 'center', transition: 'opacity .15s',
                }}>
                <span style={{ fontSize: NUM, fontWeight: 700, lineHeight: 1 }}>{g.etiqueta}</span>
                <span style={{ fontSize: TXT, fontWeight: 700, lineHeight: 1.1 }}>{st.label}</span>
                <span style={{ fontSize: TXT, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{meta}</span>
                {g.listos > 0 && (
                  <span style={{
                    position: 'absolute', top: -15, left: '50%', transform: 'translateX(-50%)', padding: '4px 9px',
                    borderRadius: 999, background: 'var(--brand)', color: 'var(--on-brand)', fontSize: 'clamp(10px, 1.3cqw, 13px)', fontWeight: 700,
                    whiteSpace: 'nowrap', boxShadow: '0 0 0 3px var(--surface)',
                  }}>{g.listos} {g.listos === 1 ? 'listo' : 'listos'}</span>
                )}
              </button>
            );
          })}
    </div>
  );
}
